import { useEffect, useRef, useCallback, useMemo } from "react";
import { useTheme, alpha } from "@mui/material";
import mapboxgl from "mapbox-gl";
import { CoamAreaFeature } from "../../geospatial/api/coamArea";

const SOURCE_ID = "network-boundaries";
const LABEL_SOURCE_ID = "network-boundaries-labels";
const FILL_LAYER_ID = "network-boundaries-fill";
const OUTLINE_LAYER_ID = "network-boundaries-outline";
const LABEL_LAYER_ID = "network-boundaries-labels-layer";
const ANIMATION_DURATION = 1000;
const PADDING = { top: 50, bottom: 50, left: 50, right: 50 };

interface UseNetworkBoundariesLayerOptions {
  map: mapboxgl.Map | null;
  features: CoamAreaFeature[];
}

export const useNetworkBoundariesLayer = ({
  map,
  features,
}: UseNetworkBoundariesLayerOptions) => {
  const theme = useTheme();
  const hoveredIdRef = useRef<string | null>(null);
  const eventHandlersRef = useRef<{
    handleMouseMove: ((e: mapboxgl.MapLayerMouseEvent) => void) | null;
    handleMouseLeave: (() => void) | null;
    handleClick: ((e: mapboxgl.MapLayerMouseEvent) => void) | null;
  }>({
    handleMouseMove: null,
    handleMouseLeave: null,
    handleClick: null,
  });

  const COLORS = useMemo(
    () => ({
      fill: alpha(theme.palette.info.main, 0.08),
      fillHover: alpha(theme.palette.info.main, 0.15),
      stroke: alpha(theme.palette.info.main, 0.4),
      strokeHover: theme.palette.info.main,
    }),
    [theme.palette.info.main]
  );

  const createFeatureCollection = useCallback(
    (geoFeatures: CoamAreaFeature[]) => ({
      type: "FeatureCollection" as const,
      features: geoFeatures.map((feature) => ({
        ...feature,
        id: feature.properties.id.toString(),
      })),
    }),
    []
  );

  const createLabelFeatureCollection = useCallback(
    (geoFeatures: CoamAreaFeature[]) => ({
      type: "FeatureCollection" as const,
      features: geoFeatures
        .filter((feature) => {
          const lng = parseFloat(feature.properties.longitudeCentroid);
          const lat = parseFloat(feature.properties.latitudeCentroid);
          return !isNaN(lng) && !isNaN(lat);
        })
        .map((feature) => ({
          type: "Feature" as const,
          id: feature.properties.id.toString(),
          geometry: {
            type: "Point" as const,
            coordinates: [
              parseFloat(feature.properties.longitudeCentroid),
              parseFloat(feature.properties.latitudeCentroid),
            ],
          },
          properties: {
            name: feature.properties.name,
            id: feature.properties.id,
          },
        })),
    }),
    []
  );

  const calculateBounds = useCallback(
    (geometry: CoamAreaFeature["geometry"]): mapboxgl.LngLatBounds | null => {
      try {
        const bounds = new mapboxgl.LngLatBounds();

        if (geometry.type === "Polygon") {
          const coordinates = geometry.coordinates as number[][][];
          coordinates.forEach((ring) => {
            ring.forEach((coord) => {
              bounds.extend([coord[0], coord[1]]);
            });
          });
        } else if (geometry.type === "MultiPolygon") {
          const coordinates = geometry.coordinates as number[][][][];
          coordinates.forEach((polygon) => {
            polygon.forEach((ring) => {
              ring.forEach((coord) => {
                bounds.extend([coord[0], coord[1]]);
              });
            });
          });
        }

        return bounds.isEmpty() ? null : bounds;
      } catch (error) {
        console.error("Error calculating bounds:", error);
        return null;
      }
    },
    []
  );

  const detachEventHandlers = useCallback((mapInstance: mapboxgl.Map) => {
    const handlers = eventHandlersRef.current;
    if (handlers.handleMouseMove) {
      mapInstance.off("mousemove", FILL_LAYER_ID, handlers.handleMouseMove);
    }
    if (handlers.handleMouseLeave) {
      mapInstance.off("mouseleave", FILL_LAYER_ID, handlers.handleMouseLeave);
    }
    if (handlers.handleClick) {
      mapInstance.off("click", FILL_LAYER_ID, handlers.handleClick);
      mapInstance.off("click", OUTLINE_LAYER_ID, handlers.handleClick);
    }
    eventHandlersRef.current = {
      handleMouseMove: null,
      handleMouseLeave: null,
      handleClick: null,
    };
  }, []);

  const attachEventHandlers = useCallback(
    (mapInstance: mapboxgl.Map, geoFeatures: CoamAreaFeature[]) => {
      detachEventHandlers(mapInstance);

      const handleMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
        if (!e.features?.length || !e.features[0].id) return;

        const projectLayers = ["clusters", "unclustered-point"];
        const projectFeatures = mapInstance.queryRenderedFeatures(e.point, {
          layers: projectLayers.filter((layer) => mapInstance.getLayer(layer)),
        });

        if (projectFeatures.length > 0) {
          if (hoveredIdRef.current) {
            mapInstance.setFeatureState(
              { source: SOURCE_ID, id: hoveredIdRef.current },
              { hover: false }
            );
            hoveredIdRef.current = null;
          }
          return;
        }

        mapInstance.getCanvas().style.cursor = "pointer";
        const currentId = e.features[0].id as string;

        if (hoveredIdRef.current && hoveredIdRef.current !== currentId) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: hoveredIdRef.current },
            { hover: false }
          );
        }

        hoveredIdRef.current = currentId;
        mapInstance.setFeatureState(
          { source: SOURCE_ID, id: currentId },
          { hover: true }
        );
      };

      const handleMouseLeave = () => {
        mapInstance.getCanvas().style.cursor = "";
        if (hoveredIdRef.current) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: hoveredIdRef.current },
            { hover: false }
          );
          hoveredIdRef.current = null;
        }
      };

      const handleClick = (e: mapboxgl.MapLayerMouseEvent) => {
        if (!e.features?.length || !e.features[0].id) return;

        const projectLayers = ["clusters", "unclustered-point"];
        const projectFeatures = mapInstance.queryRenderedFeatures(e.point, {
          layers: projectLayers.filter((layer) => mapInstance.getLayer(layer)),
        });

        if (projectFeatures.length > 0) {
          return;
        }

        const clickedId = e.features[0].id as string;
        const clickedFeature = geoFeatures.find(
          (f) => f.properties.id === Number(clickedId)
        );

        if (clickedFeature) {
          const bounds = calculateBounds(clickedFeature.geometry);
          if (bounds) {
            mapInstance.fitBounds(bounds, {
              padding: PADDING,
              duration: ANIMATION_DURATION,
              maxZoom: 12,
            });
          } else {
            const lng = parseFloat(clickedFeature.properties.longitudeCentroid);
            const lat = parseFloat(clickedFeature.properties.latitudeCentroid);

            if (!isNaN(lng) && !isNaN(lat)) {
              mapInstance.flyTo({
                center: [lng, lat],
                zoom: 10,
                duration: ANIMATION_DURATION,
              });
            }
          }
        }
      };

      eventHandlersRef.current = {
        handleMouseMove,
        handleMouseLeave,
        handleClick,
      };

      mapInstance.on("mousemove", FILL_LAYER_ID, handleMouseMove);
      mapInstance.on("mouseleave", FILL_LAYER_ID, handleMouseLeave);
      mapInstance.on("click", FILL_LAYER_ID, handleClick);
      mapInstance.on("click", OUTLINE_LAYER_ID, handleClick);
    },
    [detachEventHandlers, calculateBounds]
  );

  const initializeLayers = useCallback(
    (mapInstance: mapboxgl.Map, geoFeatures: CoamAreaFeature[]) => {
      try {
        const featureCollection = createFeatureCollection(geoFeatures);
        const labelFeatureCollection = createLabelFeatureCollection(geoFeatures);

        if (!mapInstance.getSource(SOURCE_ID)) {
          mapInstance.addSource(SOURCE_ID, {
            type: "geojson",
            data: featureCollection as unknown as GeoJSON.FeatureCollection,
            promoteId: "id",
          });
        } else {
          const source = mapInstance.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;
          source?.setData(featureCollection as unknown as GeoJSON.FeatureCollection);
        }

        if (!mapInstance.getSource(LABEL_SOURCE_ID)) {
          mapInstance.addSource(LABEL_SOURCE_ID, {
            type: "geojson",
            data: labelFeatureCollection as unknown as GeoJSON.FeatureCollection,
            promoteId: "id",
          });
        } else {
          const labelSource = mapInstance.getSource(LABEL_SOURCE_ID) as mapboxgl.GeoJSONSource;
          labelSource?.setData(labelFeatureCollection as unknown as GeoJSON.FeatureCollection);
        }

        if (!mapInstance.getLayer(FILL_LAYER_ID)) {
          mapInstance.addLayer({
            id: FILL_LAYER_ID,
            type: "fill",
            source: SOURCE_ID,
            paint: {
              "fill-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                COLORS.fillHover,
                COLORS.fill,
              ],
              "fill-opacity": 1,
            },
          });
        }

        if (!mapInstance.getLayer(OUTLINE_LAYER_ID)) {
          mapInstance.addLayer({
            id: OUTLINE_LAYER_ID,
            type: "line",
            source: SOURCE_ID,
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                COLORS.strokeHover,
                COLORS.stroke,
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "hover"], false],
                2,
                1,
              ],
            },
          });
        }

        if (!mapInstance.getLayer(LABEL_LAYER_ID)) {
          mapInstance.addLayer({
            id: LABEL_LAYER_ID,
            type: "symbol",
            source: LABEL_SOURCE_ID,
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
              "text-size": 11,
              "text-anchor": "center",
              "text-allow-overlap": false,
              "text-ignore-placement": false,
            },
            paint: {
              "text-color": theme.palette.text.secondary,
              "text-halo-color": theme.palette.background.default,
              "text-halo-width": 1.5,
              "text-halo-blur": 0.5,
            },
            minzoom: 7,
          });
        }

        const savedHoveredId = hoveredIdRef.current;
        if (savedHoveredId) {
          mapInstance.setFeatureState(
            { source: SOURCE_ID, id: savedHoveredId },
            { hover: true }
          );
        }
      } catch (error) {
        console.error("Error initializing network boundary layers:", error);
      }
    },
    [COLORS, createFeatureCollection, createLabelFeatureCollection, theme]
  );

  useEffect(() => {
    if (!map || features.length === 0) return;

    const initializeLayersAndHandlers = () => {
      initializeLayers(map, features);
      attachEventHandlers(map, features);
    };

    if (map.loaded()) {
      initializeLayersAndHandlers();
    }

    map.on("style.load", initializeLayersAndHandlers);

    return () => {
      map.off("style.load", initializeLayersAndHandlers);
      detachEventHandlers(map);
    };
  }, [map, features, initializeLayers, attachEventHandlers, detachEventHandlers]);
};

